import { UploadDocumentIcon } from '@components/icons'
import { FileDropZone } from '@features/file-storage'
import {
  EditOrganizationAttributes,
  selOrganization,
  selUploadedOrganizationLogoFilepath
} from '@features/st-organizations/st-organization-module'
import { OrganizationSettingsScaffold } from '@features/st-organizations/st-organization-settings-scaffold'
import { useFlashState } from '@st/react-util/use-flash-state'
import { Button, TextInput } from '@st/theme'
import { isEmpty } from '@st/util/json-value'
import { useState } from 'react'
import { useProcess, useSelect } from '~/store'
import { resolvePublicStorageURL } from '~/features/file-storage/storage-paths'
import { useCaptureBrowserResource } from '~/features/browser-resources/browser-resource-boundary'

/**
 * Page that lets an accountant personalize their organization.
 * For example setting a logo, changing their name, etc.
 *
 * The clients of an accountant will see this in the whitelabel app
 * when logging in and when filling out a questionnaire
 */
export function OrganizationBrandingPage() {
  const stOrganization = useProcess('stOrganization')
  const organization = useSelect('stOrganization', selOrganization)
  const uploadedLogoFilepath = useSelect('stOrganization', selUploadedOrganizationLogoFilepath)

  if (!organization) {
    return null
  }

  const [modifications, setModifications] = useState<EditOrganizationAttributes>({
    name: organization.name,
    logo: organization.logo
  })
  const [hasSaved, setHasSaved] = useFlashState(false, 2000)

  const pendingModifications = uploadedLogoFilepath
    ? { ...modifications, logo: uploadedLogoFilepath }
    : modifications
  const formValues = { ...organization, ...pendingModifications }

  const orgNameErrorMessage = isEmpty(formValues.name) ? 'A company name is required' : null

  function onClickSave() {
    if (hasSaved) {
      return
    }
    setHasSaved(true)

    setModifications(pendingModifications)
    stOrganization.send({ type: 'updateOrganization', modifications: pendingModifications })
  }

  function onChangeField(field: keyof EditOrganizationAttributes, value: string) {
    setModifications((current) => ({ ...current, [field]: value }))
  }

  return (
    <OrganizationSettingsScaffold title="Settings">
      <h2 className="mb-5 text-xl text-gray-800">Branding</h2>

      <div className="flex w-[400px] flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-base text-gray-900">Company name</label>
          <TextInput
            className="condensed"
            placeholder="Company name"
            type="text"
            value={formValues.name || ''}
            onChange={(value) => onChangeField('name', value)}
          />

          {orgNameErrorMessage ? (
            <span className="text-base text-red-600">{orgNameErrorMessage}</span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-base text-gray-900">Reply-to email</label>
          <TextInput
            className="condensed"
            placeholder="Reply-to email"
            type="text"
            value={formValues.email || ''}
            onChange={(value) => onChangeField('email', value)}
          />

          {orgNameErrorMessage ? (
            <span className="text-base text-red-600">{orgNameErrorMessage}</span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-base text-gray-900">Logo</label>
          <BrandingImagePicker filepath={formValues.logo || undefined} />
        </div>

        <Button variant="primary" className="self-end" onClick={onClickSave}>
          {hasSaved ? 'Saved!' : 'Save changes'}
        </Button>
      </div>
    </OrganizationSettingsScaffold>
  )
}

export function BrandingImagePicker({ filepath }: { filepath?: string }) {
  const stOrganization = useProcess('stOrganization')
  const captureBrowserResource = useCaptureBrowserResource()

  function onDrop(dt: DataTransfer) {
    const file = dt.files.item(0)
    if (!file) return

    stOrganization.send({ type: 'uploadOrganizationLogo', file: captureBrowserResource(file) })
  }

  return (
    <FileDropZone className="h-32 w-32" onDrop={onDrop} accept="image/*">
      {filepath ? (
        <img src={resolvePublicStorageURL(filepath)} alt="Logo preview" />
      ) : (
        <UploadDocumentIcon />
      )}
    </FileDropZone>
  )
}
